import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider,
  Grid, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

async function apiFetch(url, options = {}) {
  const r = await fetch(url, { ...options, headers: { ...bearerHeaders(), ...(options.headers || {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.ok === false) throw new Error(j.message || j.error || `HTTP ${r.status}`);
  return j;
}

// -- Polygon area (Shoelace on spherical earth, degrees -> hectares) -----------

function polygonAreaHa(coords) {
  if (!coords || coords.length < 3) return 0;
  const R = 6378137;
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const [lng1, lat1] = coords[i];
    const [lng2, lat2] = coords[(i + 1) % coords.length];
    area += (lng2 - lng1) * (Math.PI / 180) * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
  }
  return (Math.abs(area) * R * R / 2) / 10000;
}

// -- KML parser ---------------------------------------------------------------

function kmlName(el) {
  for (const child of el.children) {
    if (child.tagName === 'name') return child.textContent.trim();
  }
  return '';
}

function directChildren(el, tag) {
  return Array.from(el.children).filter(c => c.tagName === tag);
}

function collectPolygons(folder, prefixPath, totalAreaRef) {
  const polygons = [];
  for (const pm of directChildren(folder, 'Placemark')) {
    const pmName = kmlName(pm) || 'Polygon';
    const label = prefixPath.length ? `${prefixPath.join(' / ')} / ${pmName}` : pmName;
    const polyEls = Array.from(pm.getElementsByTagName('Polygon'));
    polyEls.forEach((polyEl, idx) => {
      const coordsEl = polyEl.getElementsByTagName('coordinates')[0];
      const coordsText = coordsEl?.textContent?.trim() || '';
      const coordinates = coordsText
        .split(/\s+/)
        .map(c => c.split(',').map(Number))
        .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
        .map(([lng, lat]) => [lng, lat]);
      totalAreaRef.value += polygonAreaHa(coordinates);
      polygons.push({
        name: polyEls.length > 1 ? `${label} (${idx + 1})` : label,
        geojson: { type: 'Polygon', coordinates: [coordinates] },
      });
    });
  }
  for (const sub of directChildren(folder, 'Folder')) {
    const subName = kmlName(sub) || 'Sub-area';
    polygons.push(...collectPolygons(sub, [...prefixPath, subName], totalAreaRef));
  }
  return polygons;
}

function parseKml(kmlText) {
  const doc = new DOMParser().parseFromString(kmlText, 'application/xml');
  const parseErr = doc.getElementsByTagName('parsererror')[0];
  if (parseErr) throw new Error('Invalid KML - could not parse XML');
  const container = doc.getElementsByTagName('Document')[0] || doc.documentElement;
  if (!container) throw new Error('No <Document> element found in KML');
  const projectName = kmlName(container);
  const siteFolders = directChildren(container, 'Folder');
  if (siteFolders.length === 0) throw new Error('No top-level <Folder> elements (expected one per site)');
  const totalAreaRef = { value: 0 };
  const sites = siteFolders.map(folder => {
    const siteName = kmlName(folder) || 'Unnamed Site';
    const code = siteName.toUpperCase().replace(/[^A-Z0-9]/g, '_').replace(/_+/g, '_').slice(0, 20);
    return { code, name: siteName, polygons: collectPolygons(folder, [], totalAreaRef) };
  });
  return { projectName, sites, areaKmlHa: Math.round(totalAreaRef.value * 10) / 10 };
}

// -- KML preview --------------------------------------------------------------

function KmlPreview({ parsed, onChange, onSave, saving, savedMsg }) {
  return (
    <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        KML Preview — {parsed.sites.length} site{parsed.sites.length !== 1 ? 's' : ''}
        {parsed.projectName ? ` from "${parsed.projectName}"` : ''}
        {parsed.areaKmlHa > 0 ? ` · Total area ~ ${parsed.areaKmlHa.toLocaleString()} ha` : ''}
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Site Code</TableCell>
            <TableCell>Site Name</TableCell>
            <TableCell>Polygons</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parsed.sites.map((s, i) => (
            <TableRow key={i}>
              <TableCell>
                <TextField size="small" value={s.code}
                  onChange={e => onChange(i, 'code', e.target.value)}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }} sx={{ width: 140 }} />
              </TableCell>
              <TableCell>
                <TextField size="small" value={s.name}
                  onChange={e => onChange(i, 'name', e.target.value)} sx={{ width: 200 }} />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {s.polygons.length === 0
                    ? <Typography variant="caption" color="text.secondary">none</Typography>
                    : s.polygons.map((p, j) => <Chip key={j} label={p.name} size="small" variant="outlined" />)}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving && <CircularProgress size={16} sx={{ mr: 1 }} />}
          Import Sites from KML
        </Button>
        {savedMsg && (
          <Typography variant="body2" color={savedMsg.startsWith('Error') ? 'error' : 'success.main'}>
            {savedMsg}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

// -- Metadata form ------------------------------------------------------------

function MetadataForm({ project, onRefresh }) {
  const [form, setForm] = useState({
    name:              project.name || '',
    start_date:        project.start_date ? project.start_date.slice(0, 10) : '',
    area_ha_estimated: project.area_ha_estimated ?? '',
    description:       project.description || '',
    methodology:       (() => {
      try { const raw = project.methodology; const a = Array.isArray(raw) ? raw : JSON.parse(raw || '[]'); return [...a, '', '', ''].slice(0, 3); }
      catch { return ['', '', '']; }
    })(),
    certifications: (() => {
      try { const raw = project.certifications; const a = Array.isArray(raw) ? raw : JSON.parse(raw || '[]'); return [...a, '', '', '', '', ''].slice(0, 5); }
      catch { return ['', '', '', '', '']; }
    })(),
  });
  const [msg, setMsg] = useState('');

  const save = async () => {
    setMsg('');
    try {
      await apiFetch(`/api/access/projects/${project.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name:              form.name.trim(),
          start_date:        form.start_date || null,
          area_ha_estimated: form.area_ha_estimated !== '' ? parseFloat(form.area_ha_estimated) : null,
          description:       form.description.trim() || null,
          methodology:       form.methodology.map(s => s.trim()).filter(Boolean),
          certifications:    form.certifications.map(s => s.trim()).filter(Boolean),
        }),
      });
      setMsg('Project details saved.');
      onRefresh();
    } catch (e) { setMsg(e.message); }
  };

  const setField = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const setMethodology = (i, v) => setForm(p => { const a = [...p.methodology]; a[i] = v; return { ...p, methodology: a }; });
  const setCert = (i, v) => setForm(p => { const a = [...p.certifications]; a[i] = v; return { ...p, certifications: a }; });

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField fullWidth size="small" label="Project Name"
            value={form.name} onChange={e => setField('name', e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField fullWidth size="small" label="Estimated Start Date" type="date"
            InputLabelProps={{ shrink: true }}
            value={form.start_date} onChange={e => setField('start_date', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth size="small" label="Estimated Area (ha)" type="number"
            value={form.area_ha_estimated} onChange={e => setField('area_ha_estimated', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth size="small" label="Area from KML (ha)" disabled
            value={project.area_ha_kml != null ? `${Number(project.area_ha_kml).toLocaleString()} ha (calculated)` : 'Upload KML to calculate'}
            InputProps={{ readOnly: true }} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Description" multiline rows={3}
            value={form.description} onChange={e => setField('description', e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Methodology (up to 3)</Typography>
          <Stack spacing={1}>
            {form.methodology.map((m, i) => (
              <TextField key={i} fullWidth size="small" label={`Methodology ${i + 1}`}
                placeholder={i === 0 ? 'e.g. Verra VM0042 for soil' : i === 1 ? 'e.g. Rainbow Biochar' : 'e.g. Puro for Enhanced Rock Weathering'}
                value={m} onChange={e => setMethodology(i, e.target.value)} />
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Labels and Certifications (up to 5)</Typography>
          <Stack spacing={1}>
            {form.certifications.map((c, i) => (
              <TextField key={i} fullWidth size="small" label={`Certification ${i + 1}`}
                placeholder={i === 0 ? 'e.g. ICVCM CCP-Eligible' : i === 1 ? 'e.g. CORSIA Phase 1 Eligible' : ''}
                value={c} onChange={e => setCert(i, e.target.value)} />
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" size="small" onClick={save}>Save Project Details</Button>
        {msg && <Typography variant="body2" color={msg.toLowerCase().includes('error') || msg.toLowerCase().includes('unauthorized') ? 'error' : 'success.main'}>{msg}</Typography>}
      </Stack>
    </Box>
  );
}

// -- Main exported component --------------------------------------------------

export default function ProjectSetupTab({ projectId }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const fileInputRef = useRef();
  const [parsed, setParsed] = useState(null);
  const [kmlError, setKmlError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [newSite, setNewSite] = useState({ code: '', name: '' });
  const [siteMsg, setSiteMsg] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const j = await apiFetch(`/api/access/projects/${projectId}/detail`);
      setProject(j.project);
    } catch (e) {
      setMsg('Failed to load project details.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsed(null); setKmlError('');
    const reader = new FileReader();
    reader.onload = ev => {
      try { setParsed(parseKml(ev.target.result)); }
      catch (err) { setKmlError(err.message); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const updateSite = (i, field, value) =>
    setParsed(prev => ({ ...prev, sites: prev.sites.map((s, idx) => idx === i ? { ...s, [field]: value } : s) }));

  const saveKml = async () => {
    if (!parsed) return;
    setSaving(true); setSavedMsg('');
    try {
      const j = await apiFetch(`/api/access/projects/${projectId}/kml`, {
        method: 'POST',
        body: JSON.stringify({ sites: parsed.sites, areaKmlHa: parsed.areaKmlHa }),
      });
      const areaStr = parsed.areaKmlHa > 0 ? `, total area ~ ${parsed.areaKmlHa.toLocaleString()} ha` : '';
      setSavedMsg(`Imported: ${j.sitesCreated} new site${j.sitesCreated !== 1 ? 's' : ''}, ${j.sitesUpdated} updated, ${j.polygonsTotal} polygons${areaStr}.`);
      setParsed(null);
      load();
    } catch (e) { setSavedMsg(`Error: ${e.message}`); }
    setSaving(false);
  };

  const addSite = async () => {
    if (!newSite.code.trim() || !newSite.name.trim()) return;
    setSiteMsg('');
    try {
      await apiFetch(`/api/access/projects/${projectId}/sites`, {
        method: 'POST',
        body: JSON.stringify(newSite),
      });
      setSiteMsg('Site added.');
      setNewSite({ code: '', name: '' });
      load();
    } catch (e) { setSiteMsg(e.message); }
  };

  if (!projectId) return <Typography color="text.secondary">Select a project first.</Typography>;
  if (loading) return <CircularProgress sx={{ m: 3 }} />;
  if (!project) return <Typography color="error">{msg || 'Project not found.'}</Typography>;

  return (
    <Box>
      <MetadataForm project={project} onRefresh={load} />

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" gutterBottom>Sites</Typography>
      {project.sites?.length > 0 ? (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {project.sites.map(s => (
              <TableRow key={s.id}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{s.code}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={async () => {
                    const confirm1 = window.prompt(`Permanently delete site "${s.name}"? This removes all its polygons and assignments.\n\nType the site name to confirm:`);
                    if (confirm1 === null) return;
                    if (confirm1.trim() !== s.name) { setSiteMsg('Name did not match — delete cancelled.'); return; }
                    try {
                      const res = await apiFetch(`/api/access/projects/${projectId}/sites/${s.id}`, { method: 'DELETE' });
                      setSiteMsg('Site deleted.');
                      load();
                    } catch (e) { setSiteMsg(e.message); }
                  }}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No sites yet. Upload a KML to import sites automatically.
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
          Upload KML
        </Button>
        <Typography variant="caption" color="text.secondary">
          Document &rarr; Folder (site) &rarr; Placemark (polygon). Area calculated automatically.
        </Typography>
        <input ref={fileInputRef} type="file" accept=".kml" hidden onChange={handleFileChange} />
      </Stack>
      {kmlError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{kmlError}</Typography>}
      {savedMsg && !parsed && (
        <Typography variant="body2" color={savedMsg.startsWith('Error') ? 'error' : 'success.main'} sx={{ mt: 1 }}>
          {savedMsg}
        </Typography>
      )}
      {parsed && <KmlPreview parsed={parsed} onChange={updateSite} onSave={saveKml} saving={saving} savedMsg={savedMsg} />}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <TextField size="small" label="Code" value={newSite.code}
          onChange={e => setNewSite(s => ({ ...s, code: e.target.value }))}
          sx={{ width: 140 }} inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }} />
        <TextField size="small" label="Name" value={newSite.name}
          onChange={e => setNewSite(s => ({ ...s, name: e.target.value }))}
          sx={{ width: 200 }} />
        <Button size="small" variant="outlined" onClick={addSite}
          disabled={!newSite.code.trim() || !newSite.name.trim()}>Add Site</Button>
        {siteMsg && <Typography variant="caption" color={siteMsg.toLowerCase().includes('error') ? 'error' : 'success.main'}>{siteMsg}</Typography>}
      </Stack>
    </Box>
  );
}
